import { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Check, Loader2, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const PremiumSuccess = () => {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [premiumExpires, setPremiumExpires] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/premium/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid' || response.data.status === 'completed') {
        setStatus('success');
        setPremiumExpires(response.data.premium_expires_at);
        triggerConfetti();
        
        // Refresh user data
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="glass p-8 border border-purple-500/50 text-center max-w-md w-full fade-in">
        {status === 'checking' && (
          <>
            <Loader2 className="w-20 h-20 text-purple-400 mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Your Payment...</h2>
            <p className="text-slate-400">Please wait while we confirm your subscription.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="relative mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full mx-auto flex items-center justify-center">
                <Crown className="w-12 h-12 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <PartyPopper className="w-6 h-6 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white">Welcome to Premium!</h2>
              <PartyPopper className="w-6 h-6 text-yellow-400" />
            </div>
            <p className="text-slate-400 mb-2">Your subscription is now active!</p>
            {premiumExpires && (
              <p className="text-sm text-purple-400 mb-6">
                Active until {new Date(premiumExpires).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            )}
            
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white mb-3">Your Premium Perks:</h3>
              <ul className="text-left space-y-2">
                {[
                  'Ad-free browsing',
                  'Advanced bar filters',
                  'See who\'s at bars',
                  'Unlimited friend tags',
                  'VIP badge unlocked',
                  'Priority support'
                ].map((perk, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={() => navigate('/home')}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black"
            >
              Start Exploring
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl">😕</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h2>
            <p className="text-slate-400 mb-6">
              We couldn&apos;t confirm your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/premium')}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/home')}
                className="w-full text-slate-400 hover:text-white"
              >
                Go to Home
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default PremiumSuccess;
